import type { FC } from 'react';
import React, { useRef, useState } from 'react';
import type { SxProps, Theme } from '@mui/material';
import { alpha, Button, Chip, Popover, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Iconify } from '../Iconify';
import type { FilterDefinition } from './declarations/type';
import { FilterSwitch } from './Filters';
import { useFilterLabel } from './ActiveFilterLabel';
import { canUnsetFilter, isFilterSet, unsetFilter } from './filterState';

interface FilterPillsProps {
  items: Array<FilterDefinition<any>>;
  onChange: (value: any) => void;
  // Clears every filter of the table, not only the ones rendered here.
  clearAll?: () => void;
  sx?: SxProps<Theme>;
}

/**
 * A row of filter chips. Clicking a chip opens its editor in a popover; a set
 * chip shows its value and can be cleared from its icon. The table renders one
 * instance in its header for highlighted filters and one in the collapsible
 * filter row for the rest.
 */
export const FilterPills: FC<FilterPillsProps> = ({
  items,
  onChange,
  clearAll,
  sx,
}) => {
  const { t } = useTranslation();
  const [anchor, setAnchor] = useState<{
    key: string;
    el: HTMLElement;
  } | null>(null);
  const openFilter = items.find((filter) => filter.key === anchor?.key);

  const open = (key: string, el: HTMLElement) => setAnchor({ key, el });
  const close = () => setAnchor(null);

  return (
    <Stack sx={sx} gap={1.5}>
      <Stack direction="row" flexWrap="wrap" gap={1} alignItems="center">
        {items.map((filter) => (
          <FilterPill
            key={filter.key}
            filter={filter}
            open={filter.key === anchor?.key}
            onOpen={(el) => open(filter.key, el)}
          />
        ))}
        {clearAll && (
          <Button size="small" onClick={clearAll}>
            {t('common.clear-all')}
          </Button>
        )}
      </Stack>
      <Popover
        open={Boolean(openFilter)}
        anchorEl={anchor?.el}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: -8, horizontal: 'left' }}
        slotProps={{ paper: { sx: { p: 2, minWidth: 320 } } }}
        // The popover focuses its paper, not the editor. Land in the first
        // input, or the first button for the inline date picker.
        TransitionProps={{
          onEntered: (node) =>
            (
              node.querySelector<HTMLElement>('input') ??
              node.querySelector<HTMLElement>('button')
            )?.focus(),
        }}
      >
        {openFilter && (
          <FilterSwitch
            filter={openFilter}
            onChange={onChange}
            inlineDatePicker
          />
        )}
      </Popover>
    </Stack>
  );
};

interface FilterPillProps {
  filter: FilterDefinition<any>;
  open: boolean;
  onOpen: (el: HTMLElement) => void;
}

const FilterPill: FC<FilterPillProps> = ({ filter, open, onOpen }) => {
  const label = useFilterLabel(filter);
  const set = isFilterSet(filter);
  const clearable = set && canUnsetFilter(filter);
  // Anchor on the chip root, not the click target: the chevron icon unmounts
  // once the filter is set, and a detached anchor sends the popover to 0,0.
  const ref = useRef<HTMLDivElement>(null);
  const openPill = () => ref.current && onOpen(ref.current);
  return (
    <Chip
      ref={ref}
      label={label}
      variant="outlined"
      color={set || open ? 'primary' : 'default'}
      onClick={openPill}
      onDelete={clearable ? () => unsetFilter(filter) : openPill}
      deleteIcon={
        clearable ? undefined : (
          <Iconify icon="eva:chevron-down-fill" width={18} />
        )
      }
      sx={(theme) =>
        set
          ? { bgcolor: alpha(theme.palette.primary.main, 0.08) }
          : { bgcolor: 'background.paper' }
      }
    />
  );
};
